import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/api';
import useMessage from 'antd/es/message/useMessage';
import {
  DatePicker,
  Input,
  Select,
  Table,
  Typography,
  Space,
  Tag,
  Card,
  Button,
  Checkbox,
  Popover,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { roundToThreeDecimals } from '../../utils/roundToThreeDecimals';
import { IPartnerLog, PartnerLogType } from '../../types/partnerLog.type';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const defaultVisibleColumns = {
  date: true,
  type: true,
  amount: true,
  balanceBefore: true,
  balanceAfter: true,
  description: true,
  partnerId: false,
  ownerId: false,
};

const loadColumnVisibility = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem('partner-logs-column-visibility');
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...defaultVisibleColumns, ...parsed };
    }
  } catch (error) {
    console.warn('Failed to load column visibility from localStorage:', error);
  }
  return defaultVisibleColumns;
};

export function PartnerLog() {
  const { id, name } = useParams();
  const [logs, setLogs] = useState<IPartnerLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const debouncedSearchTerm = useDebouncedValue(searchInputValue, 300);
  const [type, setType] = useState<PartnerLogType | undefined>(undefined);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [messageApi, contextHolder] = useMessage();
  const [visibleColumns, setVisibleColumns] =
    useState<Record<string, boolean>>(loadColumnVisibility);

  const fetchLogs = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const params: any = {
        page,
        limit: pageSize,
      };

      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      if (type) {
        params.type = type;
      }

      if (dateRange) {
        params.startDate = dateRange[0].toISOString();
        params.endDate = dateRange[1].toISOString();
      }

      const response = await api.get(`/partners/${id}/logs`, { params });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error: any) {
      console.error('Error fetching partner logs:', error);
      messageApi.error(
        error.response?.data?.error || 'Ошибка при получении логов партнёра'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [id, page, pageSize, debouncedSearchTerm, type, dateRange]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    try {
      localStorage.setItem(
        'partner-logs-column-visibility',
        JSON.stringify(visibleColumns)
      );
    } catch (error) {
      console.warn('Failed to save column visibility to localStorage:', error);
    }
  }, [visibleColumns]);

  const handleSearch = (value: string) => {
    setSearchInputValue(value);
  };

  const handleTypeChange = (value: PartnerLogType | undefined) => {
    setType(value);
    setPage(1);
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
    setPage(1);
  };

  const handleColumnVisibilityChange = (key: string, checked: boolean) => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSelectAllColumns = () => {
    const newVisibility: Record<string, boolean> = {};
    allColumns.forEach(col => {
      if (col.key) {
        newVisibility[col.key] = true;
      }
    });
    setVisibleColumns(newVisibility);
  };

  const handleDeselectAllColumns = () => {
    const newVisibility: Record<string, boolean> = {};
    allColumns.forEach(col => {
      if (col.key) {
        newVisibility[col.key] = false;
      }
    });
    setVisibleColumns(newVisibility);
  };

  const allColumns = [
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Партнер',
      dataIndex: 'partnerId',
      key: 'partnerId',
      render: (partner: { name: string; surname: string }) =>
        `${partner?.name} ${partner?.surname}`,
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      render: (type: PartnerLogType) => (
        <Tag color={type === PartnerLogType.INCREASE ? 'green' : 'red'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: 'Баланс до',
      dataIndex: 'balanceBefore',
      key: 'balanceBefore',
      render: (balance: number) => roundToThreeDecimals(balance),
    },
    {
      title: 'Баланс после',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (balance: number) => roundToThreeDecimals(balance),
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Создал',
      dataIndex: 'ownerId',
      key: 'ownerId',
      render: (owner: { name: string }) => owner?.name || 'Неизвестно',
    },
  ];

  const filteredColumns = allColumns.filter(
    column => column.key && visibleColumns[column.key]
  );

  const columnSelectorContent = (
    <div style={{ width: 300, maxHeight: 400, overflowY: 'auto' }}>
      <div
        style={{
          marginBottom: 16,
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 8,
        }}
      >
        <Space>
          <Button size='small' onClick={handleSelectAllColumns}>
            Выбрать все
          </Button>
          <Button size='small' onClick={handleDeselectAllColumns}>
            Снять все
          </Button>
        </Space>
      </div>
      {allColumns.map(
        column =>
          column.key && (
            <div key={column.key} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={visibleColumns[column.key]}
                onChange={e =>
                  handleColumnVisibilityChange(column.key, e.target.checked)
                }
              >
                {column.title}
              </Checkbox>
            </div>
          )
      )}
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}

      <Card style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Логи партнёра: {name}
          </Title>
          <Popover
            content={columnSelectorContent}
            title='Выбор колонок'
            trigger='click'
            placement='bottomLeft'
          >
            <Button icon={<SettingOutlined />}>Колонки</Button>
          </Popover>
        </div>
      </Card>

      <Card>
        <Space
          direction='vertical'
          size='middle'
          style={{ width: '100%', marginBottom: 16 }}
        >
          <Space wrap>
            <Search
              placeholder='Поиск'
              allowClear
              style={{ width: 300 }}
              value={searchInputValue}
              onChange={e => setSearchInputValue(e.target.value)}
              onSearch={handleSearch}
            />

            <Select
              placeholder='Тип операции'
              allowClear
              style={{ width: 200 }}
              value={type}
              onChange={handleTypeChange}
            >
              <Option value={PartnerLogType.INCREASE}>
                {PartnerLogType.INCREASE}
              </Option>
              <Option value={PartnerLogType.DECREASE}>
                {PartnerLogType.DECREASE}
              </Option>
            </Select>

            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format='DD.MM.YYYY'
              placeholder={['Дата от', 'Дата до']}
            />
          </Space>
        </Space>

        <Table
          columns={filteredColumns}
          dataSource={logs}
          bordered
          rowKey='_id'
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setPage(1);
              }
            },
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
}
